import { Badge, Popover } from "@material-ui/core";
import { observer } from "mobx-react";
import moment from "moment";
import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import { DIALOG_TRANSITION_TIME } from "../../config";
import { t } from "../../i18n/util";
import { API } from "../../network/API";
import { ApplicationStateEnum } from "../../network/APITypes";
import { generalStore } from "../../stores/GeneralStore";
import { hrStore } from "../../stores/HrStore";
import { pollingManager } from "../../stores/PollingManager";
import { THEME } from "../../theme/ThemeStore";
import { useMobileMediaQuery } from "../hooks/useMobileMediaQuery";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { HrBotNotification } from "./Notification";
import { requestNotificationPermission } from "../util/notifications";

const LOADING_LIMIT = 10;

// How many pixel until we trigger a new load?
const LOAD_THRESHOLD_PIXEL = 500;

export const NotificationsElement = observer(() => {
    const isMobile = useMobileMediaQuery();

    const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);

    const openNotifications = Boolean(anchorEl);

    const [openDialog, setOpenDialog] = React.useState<
        | { startTime: Date; endTime: Date; id: string; applicantId: string; applicationStatus: ApplicationStateEnum }
        | undefined
    >(undefined);
    const [notifications, setNotifications] = React.useState<Array<any>>([]);
    const [total, setTotal] = React.useState(0);

    const [isLoading, setIsLoading] = React.useState(false);
    const containerRef = React.useRef<HTMLDivElement | null>(null);

    const [offset, setOffset] = React.useState<number>(0);

    React.useEffect(() => {
        pollingManager.pollNotification();
    }, []);

    const handleSubmitAppointmentConfirmationDialog = () => {
        const confirmAppointment = async () => {
            try {
                generalStore.isLoading = true;
                if (!openDialog?.id) {
                    throw new Error(t("error.noAppointmendId"));
                }

                await API.postAppointment(openDialog.applicantId, openDialog.id);

                const res = await API.getNotifications({ limit: notifications.length });
                if (res.notifications) {
                    setNotifications(res.notifications);
                }
                if (res.total) {
                    setTotal(res.total);
                }

                hrStore.forceReloadApplicationState = openDialog.applicationStatus;
            } catch (error) {
                generalStore.setError(t("error.confirmAppointment"), error);
            } finally {
                generalStore.isLoading = false;
            }
        };

        confirmAppointment();

        setTimeout(() => setOpenDialog(undefined), DIALOG_TRANSITION_TIME);
    };

    const handleCloseAppointmentConfirmationDialog = () => {
        setTimeout(() => setOpenDialog(undefined), DIALOG_TRANSITION_TIME);
    };

    const loadNotifications = async (reset?: boolean) => {
        try {
            const startOffset = reset ? 0 : offset;
            const res = await API.getNotifications({ offset: startOffset, limit: LOADING_LIMIT });

            if (res.notifications) {
                setNotifications(reset ? res.notifications : notifications.concat(res.notifications));
                setOffset(startOffset + res.notifications.length);
            }
            if (res.total) {
                setTotal(res.total);
            }
        } catch (error) {
            generalStore.setError(t("error.loadNotifications"), error);
        }
    };

    const handleScroll = async (event: React.UIEvent<HTMLDivElement>) => {
        const target: any = event.target;
        const rect = target.getBoundingClientRect();
        const bottomDistance = target.scrollHeight - Math.abs(target.scrollTop) - rect.height;

        if (bottomDistance < LOAD_THRESHOLD_PIXEL && !isLoading && total > notifications.length) {
            if (offset > 0) {
                setIsLoading(true);
                await loadNotifications();
                setIsLoading(false);
            }
        }
    };

    const handleOpenNotifications = async (event: React.MouseEvent<HTMLDivElement>) => {
        requestNotificationPermission();

        try {
            setAnchorEl(event.currentTarget);
            loadNotifications(true);
        } catch (error) {
            generalStore.setError(t("error.loadNotifications"), error);
        }
    };

    const handleCloseNotifications = async () => {
        try {
            setAnchorEl(null);
            await API.postReadNotifications();
            await hrStore.loadNotifications();
        } catch (error) {
            generalStore.setError(t("error.markNotificationsAsRead"), error);
        }
    };

    return (
        <>
            <Badge
                badgeContent={hrStore.unreadCount}
                color="primary"
                overlap="circular"
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                    handleOpenNotifications(e);
                }}
            >
                <img src={THEME.Images.icNotification} alt="notification" />
            </Badge>
            <Popover
                open={openNotifications}
                anchorEl={anchorEl}
                onClose={handleCloseNotifications}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right"
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right"
                }}
                PaperProps={{ className: "blackDalBorder" }}
            >
                <div
                    style={{
                        width: isMobile ? "unset" : "550px",
                        height: 450,
                        display: "flex",
                        flexDirection: "column",
                        padding: isMobile ? "unset" : "0 20px 32px"
                    }}
                >
                    {/* header */}
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            padding: 26,
                            justifyContent: "space-between",
                            width: "100%",
                            color: THEME.customColors.headingsDark
                        }}
                        className="body1semibold"
                    >
                        <div style={{ flexGrow: 1 }}>
                            {/* This empty div equals the width taken up by the Icon on the other side to perfectly middle the label */}
                            <div style={{ width: 24 }} />
                        </div>
                        {t("screen.dashboard.notifications")}
                        <div style={{ display: "flex", flexGrow: 1, justifyContent: "flex-end" }}>
                            <img
                                src={THEME.Images.icClose}
                                alt=""
                                style={{ cursor: "pointer" }}
                                onClick={handleCloseNotifications}
                            />
                        </div>
                    </div>
                    {/* scrollContainer */}
                    <div
                        style={{
                            position: "relative",
                            flexGrow: 1,
                            display: "flex",
                            overflow: "auto",
                            flexDirection: "column",
                            padding: isMobile ? "0 8px" : "unset"
                        }}
                        onScroll={handleScroll}
                        ref={containerRef}
                    >
                        <div style={{ height: 0, flexGrow: 1 }}>
                            {notifications.map((notification) => (
                                <HrBotNotification
                                    notification={notification}
                                    setOpenDialog={setOpenDialog}
                                    closeNotifications={handleCloseNotifications}
                                    key={uuidv4()}
                                />
                            ))}
                            <div style={{ height: 1 }} />
                        </div>
                    </div>
                </div>
            </Popover>
            <ConfirmationDialog
                isOpen={!!openDialog}
                onBack={handleCloseAppointmentConfirmationDialog}
                onSubmit={handleSubmitAppointmentConfirmationDialog}
                header={t("screen.appointmentConfirmation.confirmSingleAppointment.header")}
                submitButtonText={t("screen.dashboard.confirm")}
                label={
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            textAlign: "start"
                        }}
                    >
                        {t("screen.appointmentConfirmation.confirmSingleAppointment.text")}
                        <br />
                        <br />
                        {`${moment(openDialog?.startTime).format(t("screen.dateTimeFormat"))} - ${moment(
                            openDialog?.endTime
                        ).format(t("screen.timeFormat"))}`}
                    </div>
                }
            />
        </>
    );
});
